import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Search, CreditCard, User, LogOut, Star, Menu, X, Heart, Globe } from 'lucide-react';
import { AuthContext } from '../App';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';

const Navigation = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  const navLinks = [
    { name: t('nav.home'), path: '/', icon: Home },
    { name: t('nav.browse'), path: '/browse', icon: Search },
    { name: t('nav.subscription'), path: '/subscription', icon: Star },
  ];

  const userLinks = user ? [
    { name: t('nav.favorites'), path: '/favorites', icon: Heart },
  ] : [];

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:block fixed top-0 left-0 right-0 z-50 bg-porcelain border-b gold-border">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
              <span className="font-serif text-2xl tracking-tighter text-charcoal">Glamorous</span>
            </Link>

            <div className="flex items-center space-x-12">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`uppercase tracking-wider text-sm font-sans transition-colors duration-300 ${
                    location.pathname === link.path ? 'text-primary' : 'text-charcoal hover:text-primary'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {userLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`uppercase tracking-wider text-sm font-sans transition-colors duration-300 ${
                    location.pathname === link.path ? 'text-primary' : 'text-charcoal hover:text-primary'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center space-x-4">
              <LanguageToggle />
              {user ? (
                <>
                  <Link
                    to="/profile"
                    data-testid="nav-profile-btn"
                    className="flex items-center space-x-2 px-6 py-3 border gold-border hover:bg-primary hover:text-white transition-all duration-500 uppercase tracking-widest text-sm"
                  >
                    <User className="w-4 h-4" />
                    <span>{user.name}</span>
                  </Link>
                  <button
                    onClick={logout}
                    data-testid="nav-logout-btn"
                    className="p-3 border gold-border hover:bg-secondary hover:text-white transition-all duration-500"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <Link
                  to="/subscription"
                  data-testid="nav-subscribe-btn"
                  className="px-8 py-4 bg-secondary text-white uppercase tracking-widest font-serif text-sm hover:bg-secondary/90 transition-all duration-500"
                >
                  {t('nav.subscribe')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass-nav">
        <div className="flex items-center justify-around py-4">
          {[...navLinks, ...userLinks].map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={`flex flex-col items-center space-y-1 ${
                  location.pathname === link.path ? 'text-primary' : 'text-charcoal'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs uppercase tracking-wide">{link.name}</span>
              </Link>
            );
          })}
          {user && (
            <Link
              to="/profile"
              data-testid="mobile-nav-profile"
              className={`flex flex-col items-center space-y-1 ${
                location.pathname === '/profile' ? 'text-primary' : 'text-charcoal'
              }`}
            >
              <User className="w-5 h-5" />
              <span className="text-xs uppercase tracking-wide">{t('nav.profile')}</span>
            </Link>
          )}
          <div className="flex flex-col items-center space-y-1">
            <LanguageToggle variant="mobile" />
          </div>
        </div>
      </nav>

      {/* Spacer for fixed nav */}
      <div className="hidden md:block h-20" />
      <div className="md:hidden h-20" />
    </>
  );
};

export default Navigation;